import type {
  CoachingClip,
  CoachingNote,
  FeedbackBundle,
  FeedbackMatch,
  MatchSummary,
} from "@/lib/types";
import { noteDisplayTitle, summarySections } from "./feedbackHelpers";
import { matchLabelFor, matchesById } from "./feedbackTabs";

/**
 * Unified review-item normalization — PR 2 of the Feedback Player UX
 * redesign (`docs/FEEDBACK_PLAYER_UX_PLAN.md`, Phase 2).
 *
 * One shape for every player-facing review surface: notes, clips,
 * and match summaries collapse to a `FeedbackReviewItem`
 * the UI can sort, filter, and render uniformly. Sortable by status +
 * recency; filterable by kind / tone / status.
 *
 * Consumed by:
 *   - `FeedbackReviewTab` (this PR) — the unified Review feed.
 *   - `FeedbackOverviewTab` will adopt this shape in a follow-up
 *     (Overview ships its own narrow OverviewItem subset in PR 1).
 *   - `FeedbackHistoryTab` (PR 5) — the reviewed archive.
 *
 * The helper is intentionally pure (no React, no hooks) so the
 * non-trivial sort/status/action logic gets Vitest coverage in
 * `feedbackReviewItems.test.ts`.
 *
 * Privacy: every input is already viewer-filtered + scrubbed
 * server-side. This file does NOT read `coach_private_note` from any
 * note — defense in depth. `what_to_do_next` and `player_summary` are
 * the only longform fields templated, and both are explicitly
 * player-facing (the coach UX composes them for the player).
 */

export type FeedbackReviewItemKind = "note" | "clip" | "summary";

/** Review state for the New / Reviewed / Review pill on the card.
 * `untracked` items don't have a reviewed-id contract (clips and
 * summaries today) — the card shows a neutral "Review" label and the
 * filter "Reviewed" excludes them. */
export type FeedbackReviewItemStatus = "new" | "reviewed" | "untracked";

/** The primary action label shown on the card's CTA. */
export type FeedbackReviewPrimaryAction = "watch" | "review" | "read";

/** Tone driving the colored badge ("Positive" / "To work on" / "Goal"
 * / "Team concept"). Mirrors the mockup's tone tags. */
export type FeedbackReviewItemTone =
  | "positive"
  | "correction"
  | "goal"
  | "team"
  | "neutral";

/** The single normalized review item. */
export type FeedbackReviewItem = {
  /** Stable id of the form `<kind>-<source-id>` — never the raw
   * source id, so React keys don't collide across kinds. */
  id: string;
  kind: FeedbackReviewItemKind;
  /** Card title. Player-facing fallback chain handled at build time. */
  title: string;
  /** One-line player-facing detail. For notes we prefer
   * `what_to_do_next`; falls back to `player_summary` / clip
   * description / first summary section. */
  subtitle: string;
  /** Linked player ids from the source (empty for team-level summaries). */
  playerIds: string[];
  /** ISO timestamp used by sort tie-breaks; UI renders a short label
   * via `feedbackHelpers.shortDateLabel`. */
  dateIso: string | undefined;
  matchId: string | undefined;
  matchLabel: string | undefined;
  status: FeedbackReviewItemStatus;
  tone: FeedbackReviewItemTone;
  primaryAction: FeedbackReviewPrimaryAction;
  /** A type-pill label rendered on the card ("Video Note" / "Tactical
   * Review" / "Clip" / "Match Summary"). */
  typeLabel: string;
  /** Untagged union back-reference — lets a card render the source
   * directly (open the right modal mode, render a thumb, etc.)
   * without re-fetching. */
  source:
    | { kind: "note"; note: CoachingNote }
    | { kind: "clip"; clip: CoachingClip }
    | { kind: "summary"; summary: MatchSummary };
};

export type BuildReviewItemsInput = {
  bundle: FeedbackBundle | undefined;
  reviewedNoteIds: Set<number>;
  /** PR 7: clips can now carry review rows. Pass an empty set when
   * the caller hasn't derived clip review state yet — the helper
   * will keep clips `untracked`, preserving the PR 2 contract for
   * older callers. */
  reviewedClipIds?: Set<number>;
};

/** Build the unified review item list from a `/api/my-feedback`
 * bundle. Items are sorted by "what should a player do next?":
 *   1. New notes — direct coach observations.
 *   2. Untracked clips — review-tracking is opt-in for clips.
 *   3. Match summaries — context, not a per-item task.
 *   4. Reviewed items last.
 * Within each band, newest first by `dateIso`.
 */
export function buildFeedbackReviewItems(
  input: BuildReviewItemsInput,
): FeedbackReviewItem[] {
  const { bundle, reviewedNoteIds, reviewedClipIds } = input;
  const matches = matchesById(bundle);
  const clipIds = reviewedClipIds ?? new Set<number>();

  const items: FeedbackReviewItem[] = [];

  for (const note of bundle?.notes ?? []) {
    items.push(itemFromNote(note, matches, reviewedNoteIds));
  }
  for (const clip of bundle?.clips ?? []) {
    items.push(itemFromClip(clip, matches, clipIds));
  }
  for (const summary of bundle?.match_summaries ?? []) {
    items.push(itemFromSummary(summary, matches));
  }

  items.sort(compareReviewItems);
  return items;
}

// ---------------------------------------------------------------------------
// Sort — band-major, recency-minor.
// ---------------------------------------------------------------------------

const BAND_ORDER: Record<FeedbackReviewItemKind, number> = {
  // New-eligible kinds first; the status comparator below moves
  // reviewed items below all new items regardless of kind, so this
  // ordering only governs within-status banding (new vs new,
  // reviewed vs reviewed).
  note: 0,
  clip: 1,
  summary: 2,
};

function statusRank(status: FeedbackReviewItemStatus): number {
  // `reviewed` → 2 so it always sorts after `new` and `untracked`.
  if (status === "new") return 0;
  if (status === "untracked") return 1;
  return 2;
}

function compareReviewItems(
  a: FeedbackReviewItem,
  b: FeedbackReviewItem,
): number {
  const sa = statusRank(a.status);
  const sb = statusRank(b.status);
  if (sa !== sb) return sa - sb;
  const ba = BAND_ORDER[a.kind];
  const bb = BAND_ORDER[b.kind];
  if (ba !== bb) return ba - bb;
  // Recency — newer first. Missing dates sink to the bottom of the
  // band so they don't fight for the top with real dates.
  const ta = a.dateIso ? Date.parse(a.dateIso) : 0;
  const tb = b.dateIso ? Date.parse(b.dateIso) : 0;
  return tb - ta;
}

// ---------------------------------------------------------------------------
// Per-kind builders.
// ---------------------------------------------------------------------------

function itemFromNote(
  note: CoachingNote,
  matches: Map<string, FeedbackMatch>,
  reviewedNoteIds: Set<number>,
): FeedbackReviewItem {
  const isObservation = (note.note_context ?? "video") === "observation";
  const hasTacticalBoard =
    !!note.tactical_board_json &&
    ((note.tactical_board_json.tokens?.length ?? 0) > 0 ||
      (note.tactical_board_json.shapes?.length ?? 0) > 0);
  const isTactical = isObservation && hasTacticalBoard;

  return {
    id: `note-${note.id}`,
    kind: "note",
    title: noteDisplayTitle(note),
    subtitle: notePlayerFacingSubtitle(note),
    playerIds: note.player_ids ?? [],
    dateIso: note.event_date ?? note.created_at ?? undefined,
    matchId: note.match_id ?? undefined,
    matchLabel: matchLabelFor(matches, note.match_id),
    status: reviewedNoteIds.has(note.id) ? "reviewed" : "new",
    tone: toneFromNoteType(note.note_type),
    // Tactical observations have no video to "watch"; they're a
    // board to review. Everything else is "watch".
    primaryAction: isTactical ? "review" : "watch",
    typeLabel: isTactical
      ? "Tactical Review"
      : isObservation
        ? "Coach Observation"
        : "Video Note",
    source: { kind: "note", note },
  };
}

function itemFromClip(
  clip: CoachingClip,
  matches: Map<string, FeedbackMatch>,
  reviewedClipIds: Set<number>,
): FeedbackReviewItem {
  // PR 7: clips can now carry review rows. When the bundle's
  // `reviews[]` doesn't include a row for this clip the status is
  // `new` (was `untracked` pre-PR 7); a row makes it `reviewed`.
  // Callers that don't compute clip review state at all pass an
  // empty set, which keeps clips `new`-instead-of-untracked — a
  // small UX upgrade even when the caller hasn't migrated.
  const reviewed = reviewedClipIds.has(clip.id);
  return {
    id: `clip-${clip.id}`,
    kind: "clip",
    title: clip.title?.trim() || "Match clip",
    subtitle:
      clip.description?.trim() ||
      "A clip your coach selected from a recent match.",
    playerIds: clip.player_ids ?? [],
    dateIso: clip.created_at ?? undefined,
    matchId: clip.match_id ?? undefined,
    matchLabel: matchLabelFor(matches, clip.match_id),
    status: reviewed ? "reviewed" : "new",
    tone: "neutral",
    primaryAction: "watch",
    typeLabel: "Clip",
    source: { kind: "clip", clip },
  };
}

function itemFromSummary(
  summary: MatchSummary,
  matches: Map<string, FeedbackMatch>,
): FeedbackReviewItem {
  const firstSection = summarySections(summary)[0];
  const matchLabel = matchLabelFor(matches, summary.match_id);
  return {
    id: `summary-${summary.id}`,
    kind: "summary",
    title: matchLabel ?? "Match summary",
    subtitle:
      firstSection?.[1]?.trim() ||
      "Your coach shared a recap of the match.",
    playerIds: [],
    dateIso: summary.updated_at ?? undefined,
    matchId: summary.match_id ?? undefined,
    matchLabel,
    status: "untracked",
    tone: "team",
    primaryAction: "read",
    typeLabel: "Match Summary",
    source: { kind: "summary", summary },
  };
}

// ---------------------------------------------------------------------------
// Tone mapping.
// ---------------------------------------------------------------------------

function toneFromNoteType(
  noteType: CoachingNote["note_type"] | null | undefined,
): FeedbackReviewItemTone {
  switch (noteType) {
    case "positive":
      return "positive";
    case "correction":
      return "correction";
    case "individual_goal":
      return "goal";
    case "team_concept":
      return "team";
    default:
      return "neutral";
  }
}

// ---------------------------------------------------------------------------
// Note subtitle — prefer `what_to_do_next`.
// ---------------------------------------------------------------------------

function notePlayerFacingSubtitle(note: CoachingNote): string {
  // Plan: "Prefer `what_to_do_next` for notes if available."
  const action = note.what_to_do_next?.trim();
  if (action) return action;
  const summary = note.player_summary?.trim();
  if (summary) return summary;
  const body = note.body?.trim();
  if (body) return body;
  return (note.note_context ?? "video") === "observation"
    ? "A tactical observation for you to review."
    : "A coaching note for you to watch.";
}

// ---------------------------------------------------------------------------
// Filters — declarative predicate map for the chips.
// ---------------------------------------------------------------------------

export type FeedbackReviewFilter =
  | "all"
  | "new"
  | "video"
  | "tactical"
  | "summaries"
  | "reviewed";

/** Predicate for each filter chip. `all` is the identity. */
export function filterReviewItem(
  item: FeedbackReviewItem,
  filter: FeedbackReviewFilter,
): boolean {
  switch (filter) {
    case "all":
      return true;
    case "new":
      // Untracked clips/summaries are "new to review" too — the
      // viewer hasn't acknowledged them. Reviewed items are not new.
      return item.status !== "reviewed";
    case "video":
      // Clips + video notes. Tactical observations excluded.
      if (item.kind === "clip") return true;
      if (item.kind === "note") {
        return item.source.kind === "note" &&
          (item.source.note.note_context ?? "video") === "video";
      }
      return false;
    case "tactical":
      if (item.kind !== "note") return false;
      return item.source.kind === "note" &&
        (item.source.note.note_context ?? "video") === "observation";
    case "summaries":
      return item.kind === "summary";
    case "reviewed":
      return item.status === "reviewed";
    default:
      return true;
  }
}

/** Friendly chip labels — drives the chip strip and the active-chip
 * aria-label. */
export const FEEDBACK_REVIEW_FILTER_LABELS: Record<
  FeedbackReviewFilter,
  string
> = {
  all: "All",
  new: "New",
  video: "Video",
  tactical: "Tactical",
  summaries: "Summaries",
  reviewed: "Reviewed",
};

export const FEEDBACK_REVIEW_FILTERS: ReadonlyArray<FeedbackReviewFilter> = [
  "all",
  "new",
  "video",
  "tactical",
  "summaries",
  "reviewed",
];
